import { computed, Injectable, signal } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import {
    CanvasAction,
    CanvasCreateResult,
    CanvasProgressPhase,
    CanvasStatus,
    CanvasToolEntry,
    DisplayType,
    Flow
} from '@panaya/ai-chat/shared';

/**
 * The canvas frame's state, written only by the frame interceptor and the reset paths. There is one canvas per
 * thread, so it has no id. It knows nothing about drafts.
 */
@Injectable({ providedIn: 'root' })
export class CanvasFrameStore {
    private readonly _isOpen = signal(false);
    private readonly _displayType = signal<DisplayType | null>(null);
    private readonly _flow = signal<Flow | null>(null);
    private readonly _titleKey = signal<string | null>(null);
    private readonly _progressKeys = signal<Partial<Record<CanvasProgressPhase, string>>>({});
    private readonly _actions = signal<CanvasAction[]>([]);
    private readonly _params = signal<Record<string, unknown>>({});
    private readonly _status = signal<CanvasStatus | null>(null);
    private readonly _statusMessage = signal<string | null>(null);
    private readonly _closeResult = signal<CanvasCreateResult | null>(null);

    public readonly isOpen = this._isOpen.asReadonly();
    public readonly displayType = this._displayType.asReadonly();
    public readonly flow = this._flow.asReadonly();
    /** The registration's header key; the content's own title replaces it once there is one. */
    public readonly titleKey = this._titleKey.asReadonly();
    public readonly actions = this._actions.asReadonly();
    public readonly params = this._params.asReadonly();
    public readonly status = this._status.asReadonly();
    public readonly statusMessage = this._statusMessage.asReadonly();
    /** The result of the last `canvas_close`; survives the close so the draft card can show "Created". */
    public readonly closeResult = this._closeResult.asReadonly();
    /**
     * `isOpen` for RxJS consumers such as the layout service. Synchronous on every open and close, so it does not
     * depend on when effects flush (which differs between Angular versions).
     */
    private readonly isOpenSubject = new BehaviorSubject(false);
    public readonly isOpen$: Observable<boolean> = this.isOpenSubject.pipe(distinctUntilChanged());
    public readonly primaryAction = computed(() => this._actions().find((action) => action.primary) ?? null);

    /** A registered tool started: opens with its registration; a no-op while a canvas is open. */
    public open(entry: CanvasToolEntry): void {
        if (this._isOpen()) {
            return;
        }
        this._isOpen.set(true);
        this.isOpenSubject.next(true);
        this._displayType.set(entry.display_type);
        this._flow.set(entry.flow);
        this._titleKey.set(entry.title_key ?? null);
        this._progressKeys.set({ ...entry.progress_keys });
        this._actions.set([...entry.actions]);
        this._params.set({ ...entry.params });
        this._status.set(null);
        this._statusMessage.set(null);
        this._closeResult.set(null);
    }

    /** The registration's locale key for a progress phase, else the chat's generic key for that place. Reactive. */
    public progressKey(phase: CanvasProgressPhase, genericKey: string): string {
        return this._progressKeys()[phase] ?? genericKey;
    }

    /** `canvas_status`: a plain set; the poll repeats it, and an equal value changes nothing. */
    public setStatus(status: CanvasStatus, message?: string): void {
        this._status.set(status);
        this._statusMessage.set(message ?? null);
    }

    /**
     * A new turn starts: `awaiting` and `error` described the end of the last turn, and the new turn's poll
     * does not repeat them. `creating` stays, because the Create turn itself sets it.
     */
    public clearTurnStatus(): void {
        const status = this._status();

        if (status === 'awaiting' || status === 'error') {
            this._status.set(null);
            this._statusMessage.set(null);
        }
    }

    /** `canvas_close`: clears the frame and keeps the result. */
    public close(result: CanvasCreateResult): void {
        if (!this._isOpen()) {
            return;
        }
        this.clearFrame();
        this._closeResult.set(result);
    }

    /** Init thread and close thread: everything goes. */
    public reset(): void {
        this.clearFrame();
        this._closeResult.set(null);
    }

    private clearFrame(): void {
        this._isOpen.set(false);
        this.isOpenSubject.next(false);
        this._displayType.set(null);
        this._flow.set(null);
        this._titleKey.set(null);
        this._progressKeys.set({});
        this._actions.set([]);
        this._params.set({});
        this._status.set(null);
        this._statusMessage.set(null);
    }
}
